/**
 * ClockDashboard — the screen you land on, from "Facial Clock-In Flow" 3a.
 * @file src/web/features/clock/components/ClockDashboard.tsx
 *
 * @llm-rule WHEN: Someone opens the app and has not started (or has finished) their day
 * @llm-rule NOTE: The camera no longer opens on arrival. You see where you
 *   stand first — status, shift window, whether the location checks out — and
 *   the camera is something you choose to open. That is screen 1 of the design;
 *   the camera is screen 2 and the receipt is screen 3.
 * @llm-rule AVOID: Deciding on-site vs off-site here. This previews the fence
 *   so the button can say the right thing; the server still owns the verdict.
 */

import type { ReactNode } from 'react';
import {
  CalendarDays,
  ChevronRight,
  MapPin,
  Plane,
  Receipt,
  ScanFace,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDistance, formatDuration } from '../../../shared/domain';

export interface WeekDay {
  day: string;
  clockInAt: string;
  clockOutAt: string | null;
  kind: string;
  minutes: number;
}

/** Minutes between now and a HH:MM shift boundary today. Negative once past. */
function minutesUntil(hhmm: string | undefined): number | null {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;

  const now = new Date();
  const target = new Date(now);
  target.setHours(h, m, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / 60_000);
}

function describeGap(minutes: number): string {
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** How far through the shift we are, 0–1, for the progress rule. */
function shiftProgress(start?: string, end?: string): number {
  const toMin = (s?: string) => {
    if (!s) return null;
    const [h, m] = s.split(':').map(Number);
    return Number.isFinite(h) ? h * 60 + m : null;
  };
  const s = toMin(start);
  const e = toMin(end);
  if (s === null || e === null || e <= s) return 0;

  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  return Math.max(0, Math.min(1, (nowMin - s) / (e - s)));
}

function timeOnly(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function dayLabel(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/* -------------------------------------------------------------------------- */

function QuickAction({ to, icon: Icon, label }: { to: string; icon: typeof Plane; label: string }) {
  return (
    <Link
      to={to}
      className="flex flex-1 items-center justify-center gap-2 rounded-[14px] border border-dashed border-rule px-3 py-3 text-[13.5px] font-medium text-ink-soft transition-colors hover:border-ink-ghost hover:bg-paper-sunk"
    >
      <Icon className="size-4 flex-none text-ink-faint" strokeWidth={1.8} />
      {label}
    </Link>
  );
}

export function ClockDashboard({
  greetingLine,
  subLine,
  now,
  state,
  shiftStart,
  shiftEnd,
  weekMinutes,
  workedMinutes,
  clockInAt,
  clockOutAt,
  insideFence,
  fenceName,
  distanceM,
  locating,
  week,
  primaryLabel,
  primaryHint,
  onPrimary,
  disabled,
  banner,
  /** True when the viewer's rank has no attendance at all (Company Admin).
   *  Shift timing and geofence lines are meaningless then, so they are hidden
   *  rather than shown with figures that do not apply. */
  noAttendance = false,
}: {
  greetingLine: string;
  subLine: string;
  noAttendance?: boolean;
  /** Ticking clock, passed in so one timer drives the whole page. */
  now: Date;
  state: 'NOT_CLOCKED_IN' | 'CLOCKED_IN' | 'CLOCKED_OUT';
  shiftStart?: string;
  shiftEnd?: string;
  weekMinutes: number;
  workedMinutes: number | null;
  clockInAt: string | null;
  clockOutAt: string | null;
  insideFence: boolean | null;
  fenceName: string | null;
  distanceM: number | null;
  locating: boolean;
  week: WeekDay[];
  primaryLabel: string;
  primaryHint: string;
  onPrimary: () => void;
  disabled?: boolean;
  /** Holiday / weekly-off notice, rendered above the action. */
  banner?: ReactNode;
}) {
  const statusLabel =
    state === 'CLOCKED_IN' ? 'Clocked in' : state === 'CLOCKED_OUT' ? 'Day complete' : 'Not clocked in';

  const untilStart = minutesUntil(shiftStart);
  const untilEnd = minutesUntil(shiftEnd);
  const progress = state === 'CLOCKED_IN' ? shiftProgress(shiftStart, shiftEnd) : 0;

  // One line that answers "am I early, late, or fine?"
  const timing = (() => {
    if (noAttendance) return { text: '', tone: 'var(--ink-faint)' };
    if (state === 'CLOCKED_OUT') return { text: 'signed off for today', tone: 'var(--ink-faint)' };
    if (state === 'CLOCKED_IN') {
      if (untilEnd !== null && untilEnd > 0) {
        return { text: `${describeGap(untilEnd)} left`, tone: 'var(--ink-faint)' };
      }
      return { text: 'past shift end', tone: 'var(--dot-offsite)' };
    }
    if (untilStart === null) return { text: '', tone: 'var(--ink-faint)' };
    if (untilStart > 0) {
      return { text: `starts in ${describeGap(untilStart)}`, tone: 'var(--dot-appreciation)' };
    }
    return { text: `${describeGap(untilStart)} late`, tone: 'var(--dot-flag)' };
  })();

  return (
    <div className="flex flex-col gap-4">
      {/* ---------------------------- Who and where --------------------------- */}
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[22px] font-semibold leading-tight tracking-[-0.02em] text-ink">
            {greetingLine}
          </h1>
          <p className="truncate text-[13px] text-ink-faint">{subLine}</p>
        </div>
      </div>

      {/* ------------------------------- Status ------------------------------- */}
      <div className="rounded-[18px] border border-rule bg-paper p-4 elev-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="label-mono text-ink-ghost">Status</span>
          <span
            className="rounded-full border px-2.5 py-0.5 text-[12px] font-medium"
            style={{
              borderColor: state === 'CLOCKED_IN' ? 'var(--state-ok-border)' : 'var(--rule)',
              background: state === 'CLOCKED_IN' ? 'var(--state-ok-bg)' : 'transparent',
              color: state === 'CLOCKED_IN' ? 'var(--dot-appreciation)' : 'var(--ink-soft)',
            }}
          >
            {statusLabel}
          </span>
        </div>

        <div className="mt-2.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-clock text-[34px] font-bold leading-none text-ink">
            {now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })}
          </span>
          <span className="text-[14px] text-ink-soft">
            {now.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
          </span>
        </div>

        {(shiftStart || shiftEnd) && (
          <>
            <div className="mt-2 flex flex-wrap items-baseline justify-between gap-2 text-[13.5px]">
              <span className="font-clock text-ink-soft">
                Shift {shiftStart} – {shiftEnd}
              </span>
              {timing.text && (
                <span className="font-medium" style={{ color: timing.tone }}>
                  {timing.text}
                </span>
              )}
            </div>

            {/* The bar fills only while the shift is actually running. */}
            <div className="mt-2.5 h-[7px] overflow-hidden rounded-full border border-rule bg-paper-sunk">
              <div
                className="h-full rounded-full transition-[width] duration-700"
                style={{ width: `${progress * 100}%`, background: 'var(--brand-ramp)' }}
              />
            </div>
          </>
        )}

        {(state === 'CLOCKED_IN' || state === 'CLOCKED_OUT') && (
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 border-t border-rule pt-3 text-[12.5px] text-ink-soft">
            {clockInAt && (
              <span>
                in <b className="font-clock font-bold text-ink">{timeOnly(clockInAt)}</b>
              </span>
            )}
            {clockOutAt && (
              <span>
                out <b className="font-clock font-bold text-ink">{timeOnly(clockOutAt)}</b>
              </span>
            )}
            {workedMinutes !== null && (
              <span>
                today <b className="font-clock font-bold text-ink">{formatDuration(workedMinutes)}</b>
              </span>
            )}
            <span className="ml-auto">
              week <b className="font-clock font-bold text-ink">{formatDuration(weekMinutes)}</b>
            </span>
          </div>
        )}
      </div>

      {banner}

      {/* ------------------------------ The action ---------------------------- */}
      <button
        type="button"
        onClick={onPrimary}
        disabled={disabled}
        className="group flex items-center gap-4 rounded-[18px] p-4 text-left text-white transition-all hover:brightness-[1.08] active:scale-[0.995] disabled:cursor-not-allowed disabled:opacity-60"
        style={{ background: 'var(--brand-ramp)', boxShadow: 'var(--elev-2)' }}
      >
        <span className="grid size-[58px] flex-none place-items-center rounded-full border-2 border-white/70">
          <ScanFace className="size-7" strokeWidth={1.6} />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-[19px] font-semibold leading-tight">{primaryLabel}</span>
          <span className="block text-[13px] text-white/80">{primaryHint}</span>
        </span>

        <ChevronRight
          className="size-5 flex-none text-white/70 transition-transform group-hover:translate-x-0.5"
          strokeWidth={2.2}
        />
      </button>

      {/* Where you are, in one line. */}
      <div className="flex items-center gap-2.5 px-1 text-[13px]">
        <span
          className="size-2.5 flex-none rounded-full"
          style={{
            background: locating
              ? 'var(--dot-neutral)'
              : insideFence
                ? 'var(--dot-appreciation)'
                : 'var(--dot-offsite)',
          }}
        />
        <MapPin className="size-3.5 flex-none text-ink-ghost" strokeWidth={1.9} />
        <span className="min-w-0 truncate text-ink-soft">
          {noAttendance
            ? 'Location is not tracked for your role'
            : locating
              ? 'Checking your location…'
              : insideFence === null
                ? 'No office set for you yet — any clock-in counts as off-site'
                : insideFence
                  ? `Location verified · inside ${fenceName ?? 'the office'}`
                  : `${formatDistance(distanceM)} from ${fenceName ?? 'the office'} · will log as off-site`}
        </span>
      </div>

      {/* ---------------------------- Quick actions --------------------------- */}
      <div className="flex gap-2.5">
        {/* Leave follows attendance — a rank that is not on the clock has
            nothing to take leave from, so offering it would dead-end. */}
        {!noAttendance && <QuickAction to="/leave" icon={Plane} label="Request leave" />}
        <QuickAction to="/calendar" icon={CalendarDays} label="My timesheet" />
        <QuickAction to="/payslips" icon={Receipt} label="My payslips" />
      </div>

      {/* ------------------------------ This week ----------------------------- */}
      <div className="rounded-[18px] border border-rule bg-paper p-4 elev-1">
        <div className="flex items-baseline justify-between">
          <span className="label-mono text-ink-ghost">This week</span>
          <span className="font-clock text-[12px] text-ink-faint">{formatDuration(weekMinutes)}</span>
        </div>

        {week.length === 0 ? (
          <p className="py-5 text-center text-[13px] text-ink-faint">
            Nothing recorded yet this week.
          </p>
        ) : (
          <ul className="mt-1.5">
            {week.map((row) => {
              const late = Boolean(shiftStart && timeOnly(row.clockInAt) > shiftStart);
              return (
                <li
                  key={row.day}
                  className="flex items-baseline justify-between gap-3 border-b border-dashed border-rule py-2.5 last:border-0"
                >
                  <span className="min-w-0 truncate text-[13.5px] text-ink">
                    {dayLabel(row.day)}
                    <span className="font-clock ml-2 text-ink-soft">
                      {timeOnly(row.clockInAt)} / {row.clockOutAt ? timeOnly(row.clockOutAt) : '—'}
                    </span>
                  </span>

                  <span className="flex-none text-[12px] font-medium">
                    {row.kind === 'OFF_SITE' ? (
                      <span style={{ color: 'var(--dot-offsite)' }}>off-site</span>
                    ) : late ? (
                      <span style={{ color: 'var(--dot-flag)' }}>late</span>
                    ) : (
                      <span className="text-ink-ghost">✓</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
